import { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

/**
 * ─────────────────────────────────────────────────────────────────────────
 * DATA MODEL
 * ─────────────────────────────────────────────────────────────────────────
 */
export interface Siswa {
  nama: string;
  angkatan: string;
  titel: string;
  nickname: string;
  kelas: string;
  nis: string;
  lahir: string; // ISO date string, e.g. "2008-04-12"
  alamat: string;
  pekerjaan: string;
  handphone: string;
  email: string;
  status: "Aktif" | "Lulus" | "Cuti" | "Nonaktif";
}

/** Breakpoints that map 1:1 to Tailwind's default scale. */
type Breakpoint = "mobile" | "sm" | "md" | "lg";

interface ColumnDef {
  key: keyof Siswa;
  label: string;
  /** Smallest breakpoint at which this column becomes visible. */
  minBreakpoint: Breakpoint;
  /** If true, only admins ever see this column, at any breakpoint. */
  adminOnly?: boolean;
  /** Optional custom cell renderer. */
  render?: (row: Siswa) => React.ReactNode;
  /** Right/left align, mainly for numeric-ish columns. */
  align?: "left" | "right" | "center";
}

/**
 * ─────────────────────────────────────────────────────────────────────────
 * COLUMN CONFIG — the single source of truth
 * Add/remove/reorder columns here; visibility follows automatically.
 * ─────────────────────────────────────────────────────────────────────────
 */
const COLUMNS: ColumnDef[] = [
  { key: "nama", label: "Nama", minBreakpoint: "mobile" },
  { key: "angkatan", label: "Angkatan", minBreakpoint: "mobile" },

  // sm and up (admin only)
  { key: "kelas", label: "Kelas", minBreakpoint: "sm", adminOnly: true },
  {
    key: "status",
    label: "Status",
    minBreakpoint: "sm",
    adminOnly: true,
    render: (row) => <StatusBadge status={row.status} />,
  },

  // md and up (admin only)
  { key: "nickname", label: "Nickname", minBreakpoint: "md", adminOnly: true },
  { key: "nis", label: "NIS", minBreakpoint: "md", adminOnly: true },
  { key: "pekerjaan", label: "Pekerjaan", minBreakpoint: "md", adminOnly: true },

  // lg and up (admin only)
  { key: "titel", label: "Titel", minBreakpoint: "lg", adminOnly: true },
  { key: "handphone", label: "Handphone", minBreakpoint: "lg", adminOnly: true },
  { key: "email", label: "Email", minBreakpoint: "lg", adminOnly: true },
  { key: "lahir", label: "Lahir", minBreakpoint: "lg", adminOnly: true },
  { key: "alamat", label: "Alamat", minBreakpoint: "lg", adminOnly: true },
];

/** Tailwind visibility classes per breakpoint. `mobile` = always visible. */
const BREAKPOINT_CLASS: Record<Breakpoint, string> = {
  mobile: "table-cell",
  sm: "hidden sm:table-cell",
  md: "hidden md:table-cell",
  lg: "hidden lg:table-cell",
};

function StatusBadge({ status }: { status: Siswa["status"] }) {
  const variant: Record<Siswa["status"], string> = {
    Aktif: "bg-emerald-100 text-emerald-700 hover:bg-emerald-100",
    Lulus: "bg-blue-100 text-blue-700 hover:bg-blue-100",
    Cuti: "bg-amber-100 text-amber-700 hover:bg-amber-100",
    Nonaktif: "bg-zinc-100 text-zinc-500 hover:bg-zinc-100",
  };
  return <Badge className={`font-normal ${variant[status]}`}>{status}</Badge>;
}

/**
 * ─────────────────────────────────────────────────────────────────────────
 * TABLE COMPONENT
 * ─────────────────────────────────────────────────────────────────────────
 */
interface ResponsiveDataTableProps {
  data: Siswa[];
  /** Non-admins are hard-locked to Nama + Angkatan, at every breakpoint. */
  isAdmin: boolean;
}

export function ResponsiveDataTable({ data, isAdmin }: ResponsiveDataTableProps) {
  // Non-admins never see admin-only columns — this filter happens
  // regardless of viewport width, so there is no breakpoint where a
  // restricted column can leak through.
  const visibleColumns = useMemo(
    () => COLUMNS.filter((col) => isAdmin || !col.adminOnly),
    [isAdmin]
  );

  // Non-admin view is intentionally a plain 2-column table — no need to
  // fight responsive `hidden` classes when the column set never grows.
  if (!isAdmin) {
    return (
      <div className="w-full max-w-sm rounded-lg border border-zinc-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nama</TableHead>
              <TableHead>Angkatan</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row, i) => (
              <TableRow key={i}>
                <TableCell className="font-medium text-zinc-900">{row.nama}</TableCell>
                <TableCell className="text-zinc-600">{row.angkatan}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-auto rounded-lg border border-zinc-200">
      <Table>
        <TableHeader>
          <TableRow>
            {visibleColumns.map((col) => (
              <TableHead
                key={col.key}
                className={`${BREAKPOINT_CLASS[col.minBreakpoint]} whitespace-nowrap ${
                  col.align === "right" ? "text-right" : ""
                }`}
              >
                {col.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((row, i) => (
            <TableRow key={i}>
              {visibleColumns.map((col) => (
                <TableCell
                  key={col.key}
                  className={`${BREAKPOINT_CLASS[col.minBreakpoint]} ${
                    col.key === "nama" ? "font-medium text-zinc-900" : "text-zinc-600"
                  } ${col.align === "right" ? "text-right" : ""}`}
                >
                  {col.render ? col.render(row) : String(row[col.key])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * ─────────────────────────────────────────────────────────────────────────
 * DEMO PAGE — sample data + an admin/non-admin toggle so you can see both
 * states without wiring up real auth. Delete this section in production
 * and just render <ResponsiveDataTable data={...} isAdmin={...} /> with
 * your real data and role from context/auth.
 * ─────────────────────────────────────────────────────────────────────────
 */
const SAMPLE_DATA: Siswa[] = [
  {
    nama: "Ahmad Fauzi",
    angkatan: "2021",
    titel: "S.Kom",
    nickname: "Fauzi",
    kelas: "XII RPL 1",
    nis: "2021001",
    lahir: "2003-05-14",
    alamat: "Jl. Merdeka No. 12, Bandung",
    pekerjaan: "Mahasiswa",
    handphone: "0812-3456-7890",
    email: "ahmad.fauzi@example.com",
    status: "Aktif",
  },
  {
    nama: "Siti Nurhaliza",
    angkatan: "2020",
    titel: "-",
    nickname: "Siti",
    kelas: "XII IPA 2",
    nis: "2020045",
    lahir: "2002-11-02",
    alamat: "Jl. Asia Afrika No. 5, Bandung",
    pekerjaan: "Guru Honorer",
    handphone: "0813-2233-4455",
    email: "siti.n@example.com",
    status: "Lulus",
  },
  {
    nama: "Budi Santoso",
    angkatan: "2022",
    titel: "-",
    nickname: "Budi",
    kelas: "XI IPS 1",
    nis: "2022078",
    lahir: "2005-01-27",
    alamat: "Jl. Dago No. 88, Bandung",
    pekerjaan: "Pelajar",
    handphone: "0857-1122-3344",
    email: "budi.s@example.com",
    status: "Cuti",
  },
];

export default function DemoPageResponsiveDataTable() {
  const [isAdmin, setIsAdmin] = useState(true);

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-zinc-900">Daftar Anggota</h1>
        <button
          onClick={() => setIsAdmin((v) => !v)}
          className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-50"
        >
          View as: {isAdmin ? "Admin" : "Non-admin"} (click to toggle)
        </button>
      </div>

      <ResponsiveDataTable data={SAMPLE_DATA} isAdmin={isAdmin} />

      {isAdmin && (
        <p className="text-xs text-zinc-500">
          Resize the browser: mobile shows Nama + Angkatan, sm adds Kelas + Status,
          md adds Nickname + NIS + Pekerjaan, lg adds Titel, Handphone, Email, Lahir, Alamat.
        </p>
      )}
    </div>
  );
}
